import { mapResourceTypeToApiPath } from './resourceTypeMapper';

/** Minimal shape needed to build export selectors from a selected resource. */
export interface SelectedResourceLike {
  id: string;
  name: string;
  /** Display type (e.g. "Flows"), mapped to the API path internally. */
  type: string;
}

/** Display-only id→name pair the pre-export views use to show names instead of raw ids. */
export interface ResourceDetail {
  resourceType: string;
  resourceId: string;
  resourceName: string;
}

export interface BuildMigrationResourceStringsResult {
  resourceStrings: string[];
  resourceDetails: ResourceDetail[];
}

/**
 * Builds the `resources` selector array (and display-name details) for a migration/export payload.
 *
 * When every resource of a type is selected the payload normally collapses to a `type::*` wildcard.
 * That optimization is only valid org-wide: in a Division-to-Division migration the picker (and the
 * per-type totals) are scoped to the source division, so "all selected" means "all in this division",
 * NOT all in the org. Emitting `type::*` there makes the exporter pull every resource of that type
 * org-wide. Pass `isDivisionScoped: true` to suppress the wildcard so only the chosen ids are exported.
 */
export function buildMigrationResourceStrings(
  selectedResources: SelectedResourceLike[],
  totalCountForAllTypes: Map<string, number> | undefined,
  isDivisionScoped: boolean
): BuildMigrationResourceStringsResult {
  const resourceStrings: string[] = [];
  const resourceDetails: ResourceDetail[] = [];

  const resourcesByType = new Map<string, SelectedResourceLike[]>();
  selectedResources.forEach((resource) => {
    const existing = resourcesByType.get(resource.type) || [];
    resourcesByType.set(resource.type, [...existing, resource]);
  });

  resourcesByType.forEach((selectedOfType, displayType) => {
    const apiType = mapResourceTypeToApiPath(displayType);

    const isAllSelectedResources =
      totalCountForAllTypes !== undefined &&
      totalCountForAllTypes.get(displayType) !== undefined &&
      selectedOfType.length === totalCountForAllTypes.get(displayType);

    if (isAllSelectedResources && !isDivisionScoped) {
      // All resources of this type selected (org-wide) - use wildcard
      resourceStrings.push(`${apiType}::*`);
    } else {
      // Partial selection, or any division-scoped selection - add individual resources
      selectedOfType.forEach((resource) => {
        resourceStrings.push(`${apiType}::${resource.id}`);
        resourceDetails.push({
          resourceType: apiType,
          resourceId: resource.id,
          resourceName: resource.name
        });
      });
    }
  });

  return { resourceStrings, resourceDetails };
}
